import { FilterSource } from '@enums/filter-source';
import { FilterSources } from '@interfaces/models/filter-sources';
import { ElasticSearch, ElasticSearchFilterKeys } from '@interfaces/models/elasticSearch';

// Returns the canonical string keys used inside FilterSources for a given filter value.
//   string[]                -> stringified entries (e.g. ['2','5'])
//   boolean                 -> ['true'] / ['false']
//   { '>=':100, '<=':500 }  -> ['>=', '<=']
const getStringifiedFilterKeys = (filterValue: unknown): string[] => {
  if (Array.isArray(filterValue)) {
    return filterValue.map((v) => String(v));
  }
  if (typeof filterValue === 'boolean') {
    return [String(filterValue)];
  }
  if (filterValue !== null && typeof filterValue === 'object') {
    return Object.keys(filterValue as Record<string, unknown>);
  }
  return [];
};

// Tag every value in `filters` with `source`. Used at entry points (URL Analysis, URL parsing, etc.).
export const tagAllAsSource = (
  filters: ElasticSearch['filters'] | null | undefined,
  source: FilterSource,
): FilterSources => {
  if (!filters) {
    return {};
  }
  const result: FilterSources = {};
  for (const key of Object.keys(filters) as ElasticSearchFilterKeys[]) {
    const stringKeys = getStringifiedFilterKeys(filters[key]);
    if (stringKeys.length === 0) {
      continue;
    }
    const inner: Record<string, FilterSource> = {};
    stringKeys.forEach((k) => {
      inner[k] = source;
    });
    result[key] = inner;
  }
  return result;
};

// Merge multiple FilterSources maps. Later arguments override earlier ones on collision (per inner key).
export const mergeFilterSources = (...maps: (FilterSources | null | undefined)[]): FilterSources => {
  const result: FilterSources = {};
  for (const map of maps) {
    if (!map) {
      continue;
    }
    for (const key of Object.keys(map) as ElasticSearchFilterKeys[]) {
      result[key] = { ...(result[key] ?? {}), ...map[key] };
    }
  }
  return result;
};

// Drop entries from `filterSources` whose corresponding filter values no longer exist in `filters`.
// Safety net used after every mutation so removed values can't leave dangling source tags behind.
export const pruneOrphanSources = (
  filters: ElasticSearch['filters'] | null | undefined,
  filterSources: FilterSources | null | undefined,
): FilterSources => {
  if (!filterSources) {
    return {};
  }
  const result: FilterSources = {};
  for (const key of Object.keys(filterSources) as ElasticSearchFilterKeys[]) {
    const sourceMap = filterSources[key];
    const filterValue = filters?.[key];
    if (filterValue === undefined || filterValue === null) {
      continue;
    }
    const validKeys = new Set(getStringifiedFilterKeys(filterValue));
    const kept: Record<string, FilterSource> = {};
    for (const [valKey, src] of Object.entries(sourceMap)) {
      if (validKeys.has(valKey)) {
        kept[valKey] = src;
      }
    }
    if (Object.keys(kept).length > 0) {
      result[key] = kept;
    }
  }
  return result;
};

export type SourceAddition = {
  key: ElasticSearchFilterKeys;
  values: string[];
  source: FilterSource;
};

// Drop orphans (values no longer present) and then layer the listed additions on top.
// Use this after every `setFilters` call to keep the sources map in sync.
export const syncFilterSources = (
  newFilters: ElasticSearch['filters'] | null | undefined,
  prevSources: FilterSources | null | undefined,
  additions: SourceAddition[] = [],
): FilterSources => {
  const pruned = pruneOrphanSources(newFilters, prevSources);
  if (additions.length === 0) {
    return pruned;
  }
  const result: FilterSources = { ...pruned };
  for (const { key, values, source } of additions) {
    if (values.length === 0) {
      continue;
    }
    const inner = { ...(result[key] ?? {}) };
    values.forEach((v) => {
      inner[v] = source;
    });
    result[key] = inner;
  }
  return result;
};

// Filter `filters` down to only values whose tagged source is in `allowedSources`.
// Default = User-only — used by tracking to omit URL-derived / system filters.
export const pickFiltersBySources = (
  filters: ElasticSearch['filters'] | null | undefined,
  filterSources: FilterSources | null | undefined,
  allowedSources: FilterSource[] = [FilterSource.User],
): ElasticSearch['filters'] => {
  if (!filters) {
    return {};
  }

  // If sources have not been initialized yet, fall back to preserving the current
  // filters instead of dropping them all and reporting no active filters.
  if (!filterSources || Object.keys(filterSources).length === 0) {
    return { ...filters };
  }

  const allowed = new Set(allowedSources);
  const result: ElasticSearch['filters'] = {};

  for (const key of Object.keys(filters) as ElasticSearchFilterKeys[]) {
    const sourceMap = filterSources[key];
    const value = filters[key];

    // Preserve untagged keys when filters and filterSources are temporarily out of sync.
    if (!sourceMap) {
      // @ts-ignore — result key accepts the existing filter value for this key
      result[key] = value;
      continue;
    }

    if (Array.isArray(value)) {
      const kept = value.filter((v) => allowed.has(sourceMap[String(v)]));
      if (kept.length > 0) {
        // @ts-ignore — string-list keys accept string[]
        result[key] = kept;
      }
    } else if (typeof value === 'boolean') {
      if (allowed.has(sourceMap[String(value)])) {
        // @ts-ignore — boolean keys accept boolean
        result[key] = value;
      }
    } else if (value !== null && typeof value === 'object') {
      const kept: Record<string, number> = {};
      for (const [subKey, subVal] of Object.entries(value as Record<string, number>)) {
        if (allowed.has(sourceMap[subKey])) {
          kept[subKey] = subVal;
        }
      }
      if (Object.keys(kept).length > 0) {
        // @ts-ignore — price key accepts price object
        result[key] = kept;
      }
    }
  }

  return result;
};

// Convenience predicate — true if any user-originated filter is present.
export const hasUserOriginatedFilter = (
  filters: ElasticSearch['filters'] | null | undefined,
  filterSources: FilterSources | null | undefined,
  allowedSources: FilterSource[] = [FilterSource.User],
): boolean => {
  return Object.keys(pickFiltersBySources(filters, filterSources, allowedSources)).length > 0;
};
